import { useQuery } from '@tanstack/react-query';

import { type Session, sessionByIdOptions } from '@/entities/session';
import { transcriptMetadataOptions } from '@/entities/transcript';
import { useSheetStore } from '@/shared/stores/sheet-store';
import {
    SheetDescription,
    SheetHeader,
    SheetPanel,
    SheetTitle,
} from '@/shared/ui/panels/sheet-panel';

import { SessionDetailsHeader } from './session-details/session-details-header';
import { SessionNote } from './session-details/session-note';
import { SessionNoteLoader } from './session-details/session-note/session-note-loader';

export function SessionDetails() {
    const currentSheet = useSheetStore((state) => state.currentSheet);

    const isOpen = currentSheet?.type === 'sessions/details';
    const sheetProps = isOpen ? currentSheet : undefined;
    const sessionId = sheetProps?.data.id;

    const initialData: Session | undefined =
        sheetProps?.data?.id &&
        sheetProps.data.date &&
        sheetProps.data.duration &&
        sheetProps.data.clients
            ? {
                  id: sheetProps.data.id,
                  date: sheetProps.data?.date,
                  duration: sheetProps.data?.duration,
                  status: 'SCHEDULED', // mock
                  clients: sheetProps.data?.clients,
                  treatmentApproaches: [],
                  currentNote: null,
                  actions: [],
                  inputMethod: null,
                  transcriptDeletedAt: null,
                  isTranscriptAutoDeleted: null,
                  generatedNoteDeletedAt: null,
              }
            : undefined;

    const {
        data: session,
        isPlaceholderData,
        isPending,
    } = useQuery({
        ...sessionByIdOptions(sessionId),
        placeholderData: initialData,
    });

    const isLoadingDetails = isPending || isPlaceholderData;

    const transcriptId = !isLoadingDetails && !session?.transcriptDeletedAt ? sessionId : undefined;

    const { isLoading: isLoadingMetadata } = useQuery(transcriptMetadataOptions(transcriptId));

    return (
        <SheetPanel
            type="sessions/details"
            side="right"
            size="large"
            className="gap-0"
            showCloseButton={false}
        >
            <SheetHeader className="sr-only">
                <SheetTitle>Session Note</SheetTitle>
                <SheetDescription>View and manage session notes and transcripts.</SheetDescription>
            </SheetHeader>
            {session && <SessionDetailsHeader session={session} />}
            {isLoadingDetails || isLoadingMetadata ? (
                <SessionNoteLoader size="large" />
            ) : (
                <SessionNote />
            )}
        </SheetPanel>
    );
}
